import debounce from 'lodash.debounce';

import {
  DELETE_SINGLE_SAVED_SEARCH,
  SET_FOOTER_HISTORY_RECOS,
  SET_HF_DATA,
  SET_HF_FOOT_SUBSCRIBE_ERROR,
  SET_HF_FOOT_SUBSCRIBE_SUBMIT,
  SET_HF_LOCALE,
  SET_HF_MOBILE_BREAKPOINT,
  SET_HF_SEARCH_SUGGEST,
  SET_HF_SEARCH_SUGGEST_ACTIVE_INDEX,
  SET_HF_SEARCH_TERM,
  SET_HF_SIGN_UP_MODAL,
  SET_HF_TOPBANNER_DATA,
  SET_SAVED_SEARCHES,
  SET_SINGLE_SAVED_SEARCH,
  SHOW_HF_TOP_BANNER,
  TOGGLE_HF,
  TYPED_SEARCH
} from 'constants/reduxActions';
import {
  CART_COUNT_COOKIE,
  EXPIRE_IMMEDIATE_COOKIE_TIME,
  NEWLY_REGISTERED_COOKIE,
  OPAL_PROFILE_COOKIE
} from 'constants/cookies';
import { HF_TOPBANNER_CONTENT_TYPE_REWARDS } from 'constants/rewardsInfo';
import {
  LOCALE_EN,
  LOCALE_ES,
  SHAMELESS_PLUG_LIST_ID
} from 'constants/appConstants';
import { HYDRA_SAVE_SEARCHES } from 'constants/hydraTests';
import { trackError } from 'helpers/ErrorUtils';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { trackEvent } from 'helpers/analytics';
import { getSubsiteId, isForceRefreshed } from 'helpers/ClientUtils';
import { fetchRewardsInfo } from 'actions/account/rewards';
import { getCachedRewardsInfoAndValidate } from 'helpers/MyAccountUtils';
import { isMobileContentBreakpoint } from 'helpers/HtmlHelpers';
import { loadProductDetailPage } from 'actions/productDetail';
import { setAndStoreCookie } from 'actions/session';
import { isAssigned } from 'actions/ab';
import { PRODUCT_ASIN } from 'common/regex';
import {
  stripSpecialCharsConsolidateWhitespace
} from 'helpers';
import {
  cartCount,
  enrollRewards,
  recommendationsSearch,
  subscribeToList,
  subscribeToMarketingList } from 'apis/mafia';
import { profile } from 'apis/opal';
import { searchAutoComplete } from 'apis/calypso';
import { changeCartCount } from 'actions/cart';
import ProductUtils from 'helpers/ProductUtils';
import marketplace from 'cfg/marketplace.json';

const { siteId, search: { autoCompleteCategories }, features: { showAccountRewards, showSigninIncentive } } = marketplace;

const SEARCH_SUGGEST_DEBOUNCE = 200;

export function typedSearch() {
  return {
    type: TYPED_SEARCH
  };
}

export function searchByTerm({ term, facet }, router, { forceRefreshTypedSearch, trackEvent: fireEvent, asinFetcher } = { trackEvent, forceRefreshTypedSearch: isForceRefreshed(marketplace, 'typedSearch'), asinFetcher: loadProductDetailPage }) {
  return dispatch => {

    const strippedTerm = stripSpecialCharsConsolidateWhitespace(term);

    function typedSearchPush(url, forcedRouterMethod) {
      if (!router) {// For when remotely consumed HFs can't client route
        window.location = url;
      } else if (forcedRouterMethod && router[forcedRouterMethod]) {
        router[forcedRouterMethod](url);
      } else if (forceRefreshTypedSearch) {
        router.forceRefreshPush(url);
      } else {
        router.push(url);
      }
    }

    fireEvent('TE_SEARCHBAR_TYPE', term);
    dispatch(typedSearch());
    setHFSearchSuggestionsActiveIndex(null);
    const possibleAsin = term.toUpperCase().trim();
    // If searching by ASIN
    if (possibleAsin.match(PRODUCT_ASIN)) {
      return dispatch(asinFetcher({ asin: possibleAsin })).then(product => {
        const productUrl = ProductUtils.getProductUrlFromAsin(product, possibleAsin);
        if (productUrl) {
          // going to a product
          typedSearchPush(productUrl, 'pushPreserveAppRoot');
        } else {
          typedSearchPush(`/search?term=${encodeURIComponent(strippedTerm)}`);
        }
      }).catch(() => {
        // If the asin lookup fails, there is not much we can do except to just perform a search
        typedSearchPush(`/search?term=${encodeURIComponent(strippedTerm)}`);
      });
    } else {
      if (facet) {
        typedSearchPush(`/search/${encodeURIComponent(strippedTerm)}/filter/${facet}`, 'push');
      } else {
        typedSearchPush(`/search?term=${encodeURIComponent(strippedTerm)}`);
      }
    }
    return Promise.resolve();
  };
}

// CHECK COOKIE and parse like holmes or maybe not
function getCachedCookieDataAndValidate(cookies, name) {
  const cookieData = cookies[name];
  const hasAllCookies = cookieData && cookies['session-id'];
  try {
    const cached = JSON.parse(cookieData);
    if (hasAllCookies && cached && cached.sessionId === cookies['session-id']) {
      return cached;
    } else {
      return null;
    }
  } catch (e) {
    return null;
  }
}

function setCookieData(cookieName, data) {
  return (dispatch, getState) => {
    const { cookies: { 'session-id': sessionId } } = getState();
    const expiry = new Date();
    expiry.setMinutes(expiry.getMinutes() + 30);
    const dataToSave = {
      ...data,
      sessionId: sessionId
    };
    dispatch(setAndStoreCookie(cookieName, JSON.stringify(dataToSave), expiry));
  };
}

export function getCachedCartDataAndValidate(cookies) {
  return getCachedCookieDataAndValidate(cookies, CART_COUNT_COOKIE);
}

export function setCartCountCookie(count = 0) {
  return setCookieData(CART_COUNT_COOKIE, { count });
}

export function fetchRewardsInfoForTopBanner() {
  return (dispatch, getState) => {
    const appState = getState();
    const { cookies } = appState;

    let showTopBanner = true;
    let afterShowTopBannerAction;

    const isUserRecognized = !!cookies['x-main'];
    if (isUserRecognized) {
      if (showAccountRewards) {
        const cached = getCachedRewardsInfoAndValidate(cookies);
        if (cached) {
          afterShowTopBannerAction = setHFTopBannerContent({ bannerType: HF_TOPBANNER_CONTENT_TYPE_REWARDS, bannerContent: cached.data || cached });
        } else {
          dispatch(fetchRewardsInfo({ updateTopBannerData: true }));
          // Don't show the top banner until we get the rewards info from the server.
          showTopBanner = false;
        }
      }
    } else if (showAccountRewards || showSigninIncentive) {
      // User is not recognized.  Let's mark the rewards content as empty, so that
      // the reducer picks a generic top banner phrase instead (e.g. 'You are awesome!' or 'You should log in!')
      afterShowTopBannerAction = setHFTopBannerContent({ bannerType: HF_TOPBANNER_CONTENT_TYPE_REWARDS, bannerContent: {}, showSigninIncentive });
    }

    if (showTopBanner) {
      dispatch(showHFTopBanner());
      afterShowTopBannerAction && dispatch(afterShowTopBannerAction);
    }
  };
}

export function requestCartCountFromMafia(cookies, mafia, getCall = cartCount) {
  return dispatch => getCall(mafia, cookies)
    .then(fetchErrorMiddleware)
    .then(data => {
      const cartCount = data.totalCartItems || 0;
      dispatch(setCartCountCookie(cartCount));
      dispatch(changeCartCount(cartCount));
    });
}

export function getCartCount(getCountCall = requestCartCountFromMafia) {
  return (dispatch, getState) => {
    const { cookies, environmentConfig: { api: { mafia } },
      routing: { locationBeforeTransitions: { pathname } } } = getState();

    // Set up the cart icon on any page except /checkout.
    // If we're on /checkout ...
    if (pathname.match(/^(?:\/marty)?\/m?checkout($|\/)/)) {
      // Clear count from state and clear cookie
      dispatch(setAndStoreCookie(CART_COUNT_COOKIE, '', new Date(EXPIRE_IMMEDIATE_COOKIE_TIME)));
      dispatch(changeCartCount(null));
    }// eslint-disable-line brace-style

    // Otherwise, if we're NOT on /cart ...
    // (Cart makes its own call for the count and the HF will inherit that - see cart reducer)
    else if (!pathname.match(/^(?:\/marty)?\/cart($|\/)/)) {
      // Check cart-icon cookie for cached cartCount data
      const cached = getCachedCartDataAndValidate(cookies);
      if (cached) {
        // Cookie is valid. Let's use that.
        dispatch(changeCartCount(cached.count || 0));
      } else if (cookies['session-id']) {
        // Cookie doesn't exist or is invalid. Call API for cartCount and save to new cookie if we have a session-id
        dispatch(getCountCall(cookies, mafia));
      }
    }
  };
}

export function setHFContent(data) {
  return {
    type: SET_HF_DATA,
    data
  };
}

export function setHFTopBannerContent({ bannerType, bannerContent, showSigninIncentive }) {
  return {
    type: SET_HF_TOPBANNER_DATA,
    bannerType,
    bannerContent,
    showSigninIncentive
  };
}

export function showHFTopBanner() {
  return {
    type: SHOW_HF_TOP_BANNER
  };
}

/*
  These URL strings are done nonsensically because the Spanish site
  translates all strings, including URLs in our JS bundle. This is to
  avoid that https://github01.zappos.net/mweb/marty/issues/7787.
*/
const locales = {
  [LOCALE_EN]: {
    href: `https://${'www'}.zappos.com`
  },
  [LOCALE_ES]: {
    href: `https://${'es'}.zappos.com`
  }
};

export function setLocale(locale) {
  return {
    type: SET_HF_LOCALE,
    locale
  };
}

export function handleLocaleChange(event) {
  const { target: { value, dataset } } = event;
  const needToUpdateDomain = document.location.origin !== locales[value].href;
  return dispatch => {
    dispatch(setLocale(value));

    if (needToUpdateDomain) {// if domain different
      // Track
      const { source } = dataset;
      trackEvent('TE_HEADERFOOTER_LOCALE', `${source} ${value}`);

      // Add cookie for 30 days, ZFC should redirect to correct site based on cookie
      const monthFromNow = new Date();
      monthFromNow.setDate(monthFromNow.getDate() + 30);
      dispatch(setAndStoreCookie('locale', value, monthFromNow));

      // Go to correct site
      const locale = locales[value];
      const { location: { pathname, search } } = window;
      window.location.replace(locale.href + pathname + search);
    }
  };
}

export function setHFBreakpoint(isMobile) {
  return {
    type: SET_HF_MOBILE_BREAKPOINT,
    isMobile
  };
}

export function setHeaderFooterVisibility(isHfVisible) {
  return {
    type: TOGGLE_HF,
    isHfVisible
  };
}

export function checkForHFBreakpoint(checkBreakpoint = isMobileContentBreakpoint) {
  return (dispatch, getState) => {
    const { headerFooter: { isMobile } } = getState();
    const header = document.querySelector('[data-header-container]');
    const currentIsMobile = header ? checkBreakpoint(header) : false;
    if (isMobile !== currentIsMobile) {
      dispatch(setHFBreakpoint(currentIsMobile));
    }
  };
}

// Main search term & autocomplete shtufff
export function setHFSearchTerm(term) {
  return {
    type: SET_HF_SEARCH_TERM,
    term
  };
}

export function resetHFSearchSuggestions() {
  return dispatch => {
    dispatch(setHFSearchSuggestionsActiveIndex());
    dispatch(setHFSearchSuggestions());
  };
}

export function setHFSearchSuggestions(suggestions = [], savedSearches = [], hydraSavedSearch) {
  return {
    type: SET_HF_SEARCH_SUGGEST,
    suggestions,
    savedSearches,
    hydraSavedSearch
  };
}

export function setHFSearchSuggestionsActiveIndex(suggestionIndex = null) {
  return {
    type: SET_HF_SEARCH_SUGGEST_ACTIVE_INDEX,
    suggestionIndex
  };
}

/**
 * Call for Search Sugesstions
 * @param {object} calypso          calypso config
 * @param {string} term             term to autocomplete on
 * @param  {bool}   categories      show/hide categories
 * @param autoCompleteFetch                   api fetcher  function
 * @return {object}                           promise
 */
export function autoComplete(calypso, term, categories, criteria, autoCompleteFetch = searchAutoComplete) {
  return autoCompleteFetch(calypso, { term, categories }, criteria)
    .then(fetchErrorMiddleware);
}

// Stole this debounce setup from https://gist.github.com/krstffr/245fe83885b597aabaf06348220c2fe9
export const callSearchSuggest = debounce((dispatch, calypso, term, criteria, autoComp = autoComplete, hydraSavedSearch) => autoComp(calypso, term, autoCompleteCategories, criteria)
  .then(data => {
    // data nested inside `matches` array for categories=true calls
    const suggestions = data.matches || data;
    const savedSearches = data.savedSearches || [];
    if (suggestions) {
      dispatch(setHFSearchSuggestions(suggestions, savedSearches, hydraSavedSearch));
    } else {
      dispatch(resetHFSearchSuggestions());
    }
  })
  .catch(err => {
    trackError('NON-FATAL', `Could not call search suggestions for term: ${term}`, err);
  }), SEARCH_SUGGEST_DEBOUNCE);

export function handleHFSearchChange(e, autoComp = autoComplete) {
  const term = e.target.value;
  return (dispatch, getState) => {
    const appState = getState();
    const hydraSavedSearch = isAssigned(HYDRA_SAVE_SEARCHES, 1, appState);
    dispatch(setHFSearchSuggestionsActiveIndex(null));
    dispatch(setHFSearchTerm(term));
    if (term || hydraSavedSearch) {
      const { cookies, environmentConfig: { api: { calypso } } } = appState;
      const criteria = { xMain: cookies['x-main'], zfc: cookies['zfc'] };
      callSearchSuggest(dispatch, calypso, term, criteria, autoComp, hydraSavedSearch);
    } else {
      // clear search suggest
      dispatch(setHFSearchSuggestions());
    }
  };
}

export function setFooterSubscribeSubmitted(isFooterSubscribeSubmitted) {
  return {
    type: SET_HF_FOOT_SUBSCRIBE_SUBMIT,
    isFooterSubscribeSubmitted
  };
}

export function setFooterSubscribeError(hasFooterSubscribeError) {
  return {
    type: SET_HF_FOOT_SUBSCRIBE_ERROR,
    hasFooterSubscribeError
  };
}

// Footer email newsletter submit
export function handleSubscribeSubmit(emailAddress, subscribe = subscribeToList) {
  return (dispatch, getState) => {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();
    const subsiteId = getSubsiteId(marketplace);
    trackEvent('TE_FOOTER_SUBSCRIBE');
    return subscribe(mafia, {
      siteId,
      subsiteId,
      listIds: [SHAMELESS_PLUG_LIST_ID],
      emailAddress
    }, cookies)
      .then(fetchErrorMiddleware)
      .then(() => {
        dispatch(setFooterSubscribeSubmitted(true));
      })
      .catch(err => {
        dispatch(setFooterSubscribeError(true));
        trackError('ERROR', 'Footer newsletter subscribe failure', err);
      });
  };
}

export function setFooterHistoryRecos(footerRecos) {
  return {
    type: SET_FOOTER_HISTORY_RECOS,
    footerRecos
  };
}

export function storeSavedSearches(savedSearches) {
  return {
    type: SET_SAVED_SEARCHES,
    savedSearches
  };
}

export function storeSingleSearch(savedSearches) {
  return {
    type: SET_SINGLE_SAVED_SEARCH,
    savedSearches
  };
}

export function deleteSingleSearch(savedSearches) {
  return {
    type: DELETE_SINGLE_SAVED_SEARCH,
    savedSearches
  };
}

export function getFooterHistoryRecos(getRecos = recommendationsSearch) {
  return (dispatch, getState) => {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();
    if (cookies['x-main']) {
      return getRecos(mafia, cookies)
        .then(fetchErrorMiddleware)
        .then(data => {
          if (data['zap-hp-vh']) {
            const { recoName, recs } = data['zap-hp-vh'];
            // Create sensible link for href with reftag
            if (recs?.length) {
              recs.forEach((v, i) => {
                data['zap-hp-vh'].recs[i].link = `/product/${v.item_id}?ref=${recoName}`;
              });
              dispatch(setFooterHistoryRecos(data['zap-hp-vh']));
            }
          }
        })
        .catch(err => {
          trackError('ERROR', 'Failed to fetch footer recently viewed', err);
        });
    }
  };
}

export function setSignUpModal(isSignUpModalOpen) {
  return {
    type: SET_HF_SIGN_UP_MODAL,
    isSignUpModalOpen
  };
}

export function initSignUpModal() {
  return dispatch => {
    dispatch(setAndStoreCookie(NEWLY_REGISTERED_COOKIE, '', new Date(EXPIRE_IMMEDIATE_COOKIE_TIME)));
    dispatch(setSignUpModal(true));
  };
}

function subscribeEmail(mafia, credentials) {
  return subscribeToMarketingList(mafia, { topic: 'GENERAL_NEWS' }, credentials)
    .then(fetchErrorMiddleware)
    .then(() => {
      trackEvent('TE_FOOTER_SIGN_UP_MODAL_SUBMIT', 'email');
    })
    .catch(err => {
      trackError('ERROR', 'Failed to subscribe to marketing email list via sign up modal', err);
    });
}

function subscribeRewards(mafia, akitaKey, cookies) {
  return enrollRewards(mafia, akitaKey, cookies)
    .then(fetchErrorMiddleware)
    .then(() => {
      trackEvent('TE_FOOTER_SIGN_UP_MODAL_SUBMIT', 'rewards');
    })
    .catch(err => {
      trackError('ERROR', 'Failed to subscribe to rewards via sign up modal', err);
    });
}

export function handleSignUpModalSubmit(e, subEmail = subscribeEmail, subRewards = subscribeRewards) {
  e.preventDefault();
  const emailChecked = e.target['email'] ? e.target['email'].checked : false;
  const rewardsChecked = e.target['rewards'] ? e.target['rewards'].checked : false;
  return (dispatch, getState) => {
    const { cookies, environmentConfig: { api: { mafia }, akitaKey } } = getState();
    dispatch(setSignUpModal(false));
    if (emailChecked) {
      subEmail(mafia, cookies);
    }
    if (rewardsChecked) {
      subRewards(mafia, akitaKey, cookies);
    }
  };
}

export function getProfile(profileCall = profile) {
  return (dispatch, getState) => {
    const { environmentConfig: { api: { opal } } } = getState();
    let cacheControl;
    return profileCall(opal)
      .then(res => {
        if (res.ok || res.status === 404) {// 404s are expected for unknown/new customers
          cacheControl = res.headers.get('Cache-Control');
          return res.json();
        } else {
          throw new Error('Failed fetch call');
        }
      })
      .then(json => {
        // Get expiration value from cache control header
        // Get number value from response which is in seconds. Default to one day(86400000).
        const cacheControlInt = cacheControl ? parseInt(cacheControl.replace(/\D/g,''), 10) * 1000 : 86400000;
        const expiry = new Date(Date.now() + cacheControlInt);

        let data;
        if (json.error) {
          data = { searchProfile: { valid: false } };
        } else {
          data = { searchProfile: { ...json, valid: true } };
        }
        dispatch(setAndStoreCookie(OPAL_PROFILE_COOKIE, JSON.stringify(data), expiry));
      })
      .catch(err => {
        trackError('NON-FATAL', 'Search Profile Error:', err);
      });
  };
}

export function handleSearchKeyDown(e) {
  const { keyCode } = e;
  return (dispatch, getState) => {
    const { headerFooter: { suggestions, suggestionIndex } } = getState();
    const isNull = suggestionIndex === null;
    const curEl = document.activeElement;

    // If we're focused on search input
    if (curEl === document.getElementById('searchAll')) {
      switch (keyCode) {
        case 40:// Key down
          e.preventDefault();
          if (suggestionIndex === suggestions.length - 1) {
            dispatch(setHFSearchSuggestionsActiveIndex(null));
          } else {
            dispatch(setHFSearchSuggestionsActiveIndex(!isNull ? suggestionIndex + 1 : 0));
          }
          break;
        case 38:// Key up
          e.preventDefault();
          if (suggestionIndex === 0) {
            dispatch(setHFSearchSuggestionsActiveIndex(null));
          } else {
            dispatch(setHFSearchSuggestionsActiveIndex(!isNull ? suggestionIndex - 1 : suggestions.length - 1));
          }
          break;
        case 27:// Escape
          dispatch(setHFSearchSuggestionsActiveIndex(null));
      }
    }
  };
}
